import { useEffect, useRef, useState, ChangeEvent, KeyboardEvent } from 'react';
import { BookmarkPlus, Loader2, Paperclip, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useCampaignSendMutation, useCreateTemplateMutation } from '@/client/api/campaigns/campaigns.mutation';
import { useSendCheckQuery, useTemplateDetailQuery, useTemplateListQuery } from '@/client/api/campaigns/campaigns.query';
import { useAdminUserSearchQuery } from '@/client/api/admin/users.query';
import { useMailboxesQuery } from '@/client/api/inbox/inbox';
import useDebounce from '@/client/hooks/useDebounce';
import { ConsentStatus, EmailAttachment, RecipientRef, SendResult } from '@/client/types/campaigns';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import EmailEditor, { EmailContent } from './EmailEditor';
import { SignatureToggle } from './Signatures';

interface Recipient {
  key: string;
  label: string;
  detail?: string;
  ref: RecipientRef;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mirrors the API's checks (admin/model/messaging/service.py) so a bad file is
// refused when picked, not after Send.
const ATTACH_EXTS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'rtf', 'odt', 'ods', 'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'gif', 'ics', 'zip'];
const ATTACH_MAX_FILES = 10;
const ATTACH_MAX_BYTES = 10 * 1024 * 1024;

type PickedFile = EmailAttachment & { size: number };

const formatSize = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

const readBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',', 2)[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);

const summarizeTally = (result: SendResult) => {
  const t = result.tally;
  if (!t) return 'Sent';
  return (
    [
      t.sent != null ? `${t.sent} sent` : null,
      t.failed ? `${t.failed} failed` : null,
      t.opted_out ? `${t.opted_out} opted out` : null,
      t.suppressed ? `${t.suppressed} suppressed` : null,
      t.no_address ? `${t.no_address} no address` : null,
      t.skipped ? `${t.skipped} skipped` : null,
    ]
      .filter(Boolean)
      .join(' · ') || 'Sent'
  );
};

/**
 * "To" field: type a name or email to find a user, Enter/comma to add a typed
 * address as is. A picked user goes out as their uid, so the API resolves the
 * address and honours their preferences.
 */
const refKey = (ref: RecipientRef) => (ref.uid != null ? `uid:${ref.uid}` : `email:${String(ref.email ?? '').toLowerCase()}`);

const BLOCKED: ConsentStatus[] = ['opted_out', 'suppressed'];

function RecipientField({
  value,
  onChange,
  status = {},
}: {
  value: Recipient[];
  onChange: (next: Recipient[]) => void;
  status?: Record<string, ConsentStatus>;
}) {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const term = useDebounce(draft.trim(), 300);
  const search = useAdminUserSearchQuery(term.length >= 2 ? term : null);
  const taken = new Set(value.map((r) => r.key));
  const matches = (search.data || []).filter((u) => u.email && !taken.has(`uid:${u.uid}`)).slice(0, 8);

  const add = (r: Recipient) => {
    if (!taken.has(r.key)) onChange([...value, r]);
    setDraft('');
  };

  const addTyped = () => {
    const parts = draft.split(/[\s,;]+/).filter(Boolean);
    const bad = parts.filter((p) => !EMAIL_RE.test(p));
    const good = parts.filter((p) => EMAIL_RE.test(p) && !taken.has(`email:${p.toLowerCase()}`));
    if (good.length) {
      onChange([...value, ...good.map((email) => ({ key: `email:${email.toLowerCase()}`, label: email, ref: { email } }))]);
    }
    setDraft(bad.join(' '));
    if (bad.length) toast.error(`Not an email address: ${bad.join(', ')}`);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === 'Tab') && draft.trim()) {
      e.preventDefault();
      addTyped();
    } else if (e.key === 'Backspace' && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="relative">
      <div className="flex min-h-[42px] flex-wrap items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-blue-500">
        {value.map((r) => {
          const st = status[r.key];
          const blocked = st && BLOCKED.includes(st);
          return (
          <span
            key={r.key}
            title={blocked ? `${r.detail ?? r.label}: ${st === 'opted_out' ? 'opted out of admin email' : 'suppressed'}` : r.detail}
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-sm ${blocked ? 'bg-amber-100 text-amber-900' : 'bg-gray-100 text-gray-800'}`}
          >
            {r.label}
            {blocked && <span className="text-xs">({st === 'opted_out' ? 'opted out' : 'suppressed'})</span>}
            <button type="button" aria-label={`Remove ${r.label}`} onClick={() => onChange(value.filter((x) => x.key !== r.key))} className="text-gray-500 hover:text-gray-900">
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
          );
        })}
        <input
          type="text"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={value.length ? '' : 'Name or email address'}
          aria-label="Recipients"
          className="min-w-[12rem] flex-1 border-0 bg-transparent px-1 py-1 text-sm focus:outline-none"
        />
      </div>
      {open && term.length >= 2 && (matches.length > 0 || search.isFetching) && (
        <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {search.isFetching && matches.length === 0 && <li className="px-3 py-2 text-sm text-gray-500">Searching...</li>}
          {matches.map((u) => {
            const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
            return (
              <li key={u.uid}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => add({ key: `uid:${u.uid}`, label: name, detail: u.email, ref: { uid: u.uid } })}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50"
                >
                  <span className="block text-sm text-gray-900">{name}</span>
                  <span className="block text-xs text-gray-500">{u.email} · uid {u.uid}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function EmailComposer({ canOverride = false }: { canOverride?: boolean }) {
  const send = useCampaignSendMutation();
  const mailboxes = useMailboxesQuery().data || [];
  // '' sends from the no-reply updates address.
  const [from, setFrom] = useState<string | null>(null);
  const fromEmail = from ?? mailboxes[0]?.email ?? '';
  const [withSignature, setWithSignature] = useState(true);
  const [to, setTo] = useState<Recipient[]>([]);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState<EmailContent>({ html: '', text: '' });
  // Set only when the editor's content is replaced from outside (template, clear).
  const [loaded, setLoaded] = useState('');
  const [templateId, setTemplateId] = useState<number | null>(null);
  // Bumped after a send so the editor mounts empty.
  const [sentCount, setSentCount] = useState(0);
  const [reason, setReason] = useState('');
  // Only asked when someone on the list has opted out: skip them, or send anyway with a reason.
  const [overrideOptOuts, setOverrideOptOuts] = useState(false);
  const [files, setFiles] = useState<PickedFile[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const saveTemplate = useCreateTemplateMutation();

  const addFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    const bad = picked.find((f) => !ATTACH_EXTS.includes(f.name.split('.').pop()?.toLowerCase() || ''));
    if (bad) return toast.error(`${bad.name} is not a type that can be attached.`);
    if (files.length + picked.length > ATTACH_MAX_FILES) return toast.error(`Attach at most ${ATTACH_MAX_FILES} files.`);
    const total = [...files, ...picked].reduce((n, f) => n + f.size, 0);
    if (total > ATTACH_MAX_BYTES) return toast.error('Attachments can add up to 10 MB at most.');
    try {
      const read = await Promise.all(picked.map(async (f) => ({ name: f.name, size: f.size, content: await readBase64(f) })));
      setFiles((prev) => [...prev, ...read]);
    } catch {
      toast.error('Could not read that file.');
    }
  };

  const handleSaveTemplate = async () => {
    if (!content.text.trim()) return toast.error('Write a message to save as a template', { richColors: true });
    const name = window.prompt('Template name', subject.trim())?.trim();
    if (!name) return;
    try {
      await saveTemplate.mutateAsync({
        body: { name_system: `email_compose_${slugify(name) || Date.now()}`, name, email_html: content.html, email_text: content.text.trim() },
        idempotencyKey: generateIdempotencyKey(),
      });
      toast.success(`Saved "${name}" as a template`, { richColors: true });
    } catch (error: any) {
      toast.error(error?.message || 'Could not save the template', { richColors: true });
    }
  };

  const templates = (useTemplateListQuery({ limit: 200 }).data?.templates || []).filter((t) => t.email_html);
  const template = useTemplateDetailQuery(templateId);

  useEffect(() => {
    const html = template.data?.email_html;
    if (!html) return;
    if (content.text && !window.confirm('Replace the current message with this template?')) {
      setTemplateId(null);
      return;
    }
    setLoaded(html);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.data]);

  const check = useSendCheckQuery(
    to.map((r) => r.ref),
    ['email'],
  );
  const status: Record<string, ConsentStatus> = {};
  for (const r of check.data?.recipients ?? []) {
    if (r.channels.email) status[refKey(r.ref)] = r.channels.email;
  }
  const blocked = to.filter((r) => BLOCKED.includes(status[r.key]));
  const mayOverride = canOverride || Boolean(check.data?.can_override);
  const overriding = blocked.length > 0 && overrideOptOuts && mayOverride;

  const handleSend = async () => {
    if (draftIsIncomplete()) return;
    if (overriding && !reason.trim()) {
      toast.error('Give a reason for sending to people who opted out', { richColors: true });
      return;
    }
    if (!overriding && blocked.length && blocked.length === to.length) {
      toast.error('Everyone on the list has opted out of admin email', { richColors: true });
      return;
    }
    try {
      const result = await send.mutateAsync({
        body: {
          recipients: to.map((r) => r.ref),
          channels: ['email'],
          subject: subject.trim(),
          body: content.text.trim(),
          html: content.html,
          respect_prefs: !overriding,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          ...(fromEmail ? { from: fromEmail, signature: withSignature } : {}),
          ...(files.length ? { attachments: files.map(({ name, content }) => ({ name, content })) } : {}),
        },
        idempotencyKey: generateIdempotencyKey(),
      });
      if (result.override_denied) {
        toast.warning('Override denied: sent with preferences respected', { richColors: true });
      }
      toast.success(summarizeTally(result), { richColors: true });
      setTo([]);
      setSubject('');
      setLoaded('');
      setContent({ html: '', text: '' });
      setTemplateId(null);
      setFiles([]);
      setOverrideOptOuts(false);
      setSentCount((n) => n + 1);
    } catch (error: any) {
      toast.error(error?.message || 'Send failed', { richColors: true });
    }
  };

  const draftIsIncomplete = () => {
    const missing = !to.length ? 'Add at least one recipient' : !subject.trim() ? 'Add a subject' : !content.text.trim() ? 'Write a message' : '';
    if (missing) toast.error(missing, { richColors: true });
    return Boolean(missing);
  };

  const field = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="space-y-4 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
      <div>
        <label htmlFor="email-from" className="mb-1 block text-xs text-gray-500">From</label>
        <select id="email-from" value={fromEmail} onChange={(e) => setFrom(e.target.value)} className={`${field} bg-white`}>
          {mailboxes.map((m) => (
            <option key={m.email} value={m.email}>
              {m.name} &lt;{m.email}&gt;
            </option>
          ))}
          <option value="">FeelyFeely updates (replies are not collected)</option>
        </select>
      </div>

      <div>
        <label className="mb-1 block text-xs text-gray-500">To</label>
        <RecipientField value={to} onChange={setTo} status={status} />
      </div>

      <div className="grid gap-4 sm:grid-cols-[1fr_16rem]">
        <div>
          <label htmlFor="email-subject" className="mb-1 block text-xs text-gray-500">Subject</label>
          <input id="email-subject" type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className={field} />
        </div>
        <div>
          <label htmlFor="email-template" className="mb-1 block text-xs text-gray-500">Start from a template</label>
          <select
            id="email-template"
            value={templateId ?? ''}
            onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : null)}
            className={`${field} bg-white`}
          >
            <option value="">Blank</option>
            {templates.map((t) => (
              <option key={t.ntmplid} value={t.ntmplid}>
                {t.name || t.name_system}
                {t.locale ? ` (${t.locale})` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs text-gray-500">Message</label>
        <EmailEditor key={sentCount} value={loaded} onChange={setContent} minHeight={260} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileInput} type="file" multiple hidden accept={ATTACH_EXTS.map((x) => `.${x}`).join(',')} onChange={addFiles} />
        <Button type="button" variant="outline" size="sm" onClick={() => fileInput.current?.click()} className="gap-1.5">
          <Paperclip className="h-4 w-4" />
          Attach files
        </Button>
        {files.map((f, i) => (
          <span key={`${f.name}-${i}`} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-sm text-gray-800">
            {f.name}
            <span className="text-xs text-gray-500">{formatSize(f.size)}</span>
            <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-gray-500 hover:text-gray-900">
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
      </div>

      <SignatureToggle mailbox={mailboxes.find((m) => m.email === fromEmail)} on={withSignature} onChange={setWithSignature} />

      {blocked.length > 0 && (
        <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p>
            {blocked.length === 1 ? `${blocked[0].label} has` : `${blocked.length} recipients have`} opted out of admin email
            {blocked.length > 1 ? `: ${blocked.map((r) => r.label).join(', ')}` : ''}.
          </p>
          <label className="flex items-center gap-2">
            <input type="radio" name="optout" checked={!overrideOptOuts} onChange={() => setOverrideOptOuts(false)} />
            Skip {blocked.length === 1 ? 'them' : 'those recipients'}
            {blocked.length === to.length ? ' (nobody would receive it)' : ''}
          </label>
          <label className={`flex items-center gap-2 ${mayOverride ? '' : 'opacity-50'}`}>
            <input type="radio" name="optout" checked={overrideOptOuts} disabled={!mayOverride} onChange={() => setOverrideOptOuts(true)} />
            Send anyway: critical or transactional mail only, with a reason below
          </label>
          {!mayOverride && <p className="text-xs">Sending past an opt-out needs the messaging.override permission.</p>}
        </div>
      )}

      <div>
        <label htmlFor="email-reason" className="mb-1 block text-xs text-gray-500">
          {overriding ? 'Reason for overriding opt-outs (required, audited)' : 'Audit reason'}
        </label>
        <input
          id="email-reason"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className={`${field} ${overriding && !reason.trim() ? 'border-amber-400' : ''}`}
          placeholder={overriding ? 'e.g. security notice about their account' : 'manual support outreach'}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" disabled={saveTemplate.isPending} onClick={handleSaveTemplate} className="gap-2">
          {saveTemplate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookmarkPlus className="h-4 w-4" />}
          Save as template
        </Button>
        <Button type="button" disabled={send.isPending} onClick={handleSend} className="gap-2">
          {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send
        </Button>
      </div>
    </div>
  );
}
